import { useState, useRef, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { X, Save, Trash2, Camera, Upload, FileText, ExternalLink } from 'lucide-react';
import {
  unitDisplayTitle,
  resolveUnitOps,
  resolveUnitTypeDisplayName,
} from '../utils/unitDisplay';
import { formatUnitIdShort } from '../utils/unitIdFormat';
import AddSiblingUnitForm from './AddSiblingUnitForm';
import SsDefaultAssignee from './SsDefaultAssignee';
import UnitChecklistPicker from './UnitChecklistPicker';
import AuthImage from './AuthImage';
import { InfoButton } from './InfoModal';
import { UnitHubInfoContent } from './UnitHubInfoContent';

function parseConfirmed(unit) {
  const raw =
    unit?.confirmed_checklist ||
    unit?.confirmed_checklist_json ||
    [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try {
      const p = JSON.parse(raw);
      return Array.isArray(p) ? p : [];
    } catch {
      return [];
    }
  }
  return [];
}
export const UNIT_HUB_TABS = [
  { id: 'system-info', label: 'Set System' },
  { id: 'set-inspection-list', label: 'Set Inspection List' },
  { id: 'update-info', label: 'Update Info' },
];

export function getUnitHubTabs(unit, allowedIds = null) {
  const id = formatUnitIdShort(unit?.unique_id, unit?.ss_code) || '';
  const typeName = String(unit?.unit_type || '').trim();
  const inspectLabel = id
    ? `Set SS ${id}${typeName ? ` ${typeName}` : ''} Inspection List`
    : 'Set Inspection List';
  const all = UNIT_HUB_TABS.map((t) =>
    t.id === 'set-inspection-list' ? { ...t, label: inspectLabel } : t
  );
  if (!allowedIds || !allowedIds.length) return all;
  const allow = new Set(allowedIds.map(String));
  return all.filter((t) => allow.has(t.id));
}

export default function SsUnitDetailModal({
  unit,
  building,
  onClose,
  onSaved,
  onDeleted,
  usedUniqueIds: _usedUniqueIds = [],
  idSuffixOnly: _idSuffixOnly = false,
  showRemoveFromPlan = false,
  onRemoveFromPlan,
  asPage = false,
  hubTab: hubTabProp,
  onHubTabChange,
  /** Limit hub tabs (e.g. Floor plan: `['system-info']` only). */
  allowedHubTabs = null,
  floorUnits = [],
  siblingUnits = [],
  onOpenFloorUnit,
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const ops = resolveUnitOps(unit);
  const { data: fetchedEquipmentTypes = [] } = useQuery({
    queryKey: ['ss-equipment-types', building.id, unit.building_ss_instance_id],
    queryFn: async () => {
      const res = await api.get(
        `/buildings/${building.id}/ss-instances/${unit.building_ss_instance_id}/equipment-types`
      );
      const raw = res.data?.data ?? res.data;
      return Array.isArray(raw) ? raw : [];
    },
    enabled: !!building?.id && !!unit?.building_ss_instance_id,
  });
  const equipmentTypes = fetchedEquipmentTypes;
  const typeDisplayName = useMemo(
    () => resolveUnitTypeDisplayName(unit, { equipmentTypes }),
    [unit, equipmentTypes]
  );
  const hubTabs = useMemo(
    () => getUnitHubTabs({ ...unit, unit_type: typeDisplayName }, allowedHubTabs),
    [unit, typeDisplayName, allowedHubTabs]
  );
  const [localHubTab, setLocalHubTab] = useState('system-info');
  const hubTabAllowed = (id) => hubTabs.some((t) => t.id === id);
  const hubTab =
    hubTabProp && hubTabAllowed(hubTabProp)
      ? hubTabProp
      : hubTabAllowed(localHubTab)
        ? localHubTab
        : hubTabs[0]?.id || 'system-info';
  const setHubTab = (id) => {
    if (!hubTabAllowed(id)) return;
    if (onHubTabChange) onHubTabChange(id);
    else if (hubTabProp == null) setLocalHubTab(id);
  };
  const [form, setForm] = useState(() => ({
    name: typeDisplayName || unit.name || unit.unit_type || '',
    unique_id: unit.unique_id || '',
    location: ops.location,
    manufacturer: unit.manufacturer || '',
    model: unit.model || '',
    serial_number: unit.serial_number || '',
    unit_type: typeDisplayName || unit.unit_type || '',
    installation_year: unit.installation_year || '',
    specifications: unit.specifications || '',
    manual_path: unit.manual_path || '',
    documents_path: unit.documents_path || '',
    inspection_frequency: ops.inspection_frequency || 'ANNUAL',
    inspection_method_type: ops.inspection_method_type || 'UNIFORM',
    inspection_method_notes: ops.inspection_method_notes || '',
    floor_plan_x: unit.floor_plan_x ?? '',
    floor_plan_y: unit.floor_plan_y ?? '',
    status: unit.status || 'ACTIVE',
    level_id: unit.level_id || '',
  }));

  useEffect(() => {
    if (!typeDisplayName) return;
    setForm((prev) => {
      const syncFromType = !!unit.equipment_type_id;
      const nameNext = syncFromType ? typeDisplayName : prev.name || typeDisplayName;
      const typeNext = syncFromType ? typeDisplayName : prev.unit_type || typeDisplayName;
      if (prev.name === nameNext && prev.unit_type === typeNext) return prev;
      return { ...prev, name: nameNext, unit_type: typeNext };
    });
  }, [typeDisplayName, unit.id, unit.equipment_type_id]);

  const [confirmedChecklist, setConfirmedChecklist] = useState(() => parseConfirmed(unit));
  const [uploading, setUploading] = useState(false);
  const cameraInputRef = useRef(null);
  const photoInputRef = useRef(null);
  const docInputRef = useRef(null);

  const uploadUnitFile = async (file, field) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('type', 'ss-units');
      const res = await api.post('/files/upload', fd);
      const url = res.data?.filename || res.data?.url || res.data?.path;
      if (!url) throw new Error('No file URL returned');
      setForm((prev) => ({ ...prev, [field]: url }));
      toast.success(field === 'manual_path' ? 'Photo saved' : 'Document saved');
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };
  const invalidate = () => {
    queryClient.invalidateQueries(['building', building.id, 'ss-units']);
    queryClient.invalidateQueries(['building', building.id]);
  };

  const saveMutation = useMutation({
    mutationFn: async (payload) => {
      const response = await api.put(`/ss-units/${unit.id}`, payload);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Unit updated');
      invalidate();
      onSaved?.();
      if (!asPage) onClose?.();
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to update unit'),
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await api.delete(`/ss-units/${unit.id}`);
    },
    onSuccess: () => {
      toast.success('Unit removed');
      invalidate();
      onDeleted?.();
      onClose();
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to delete unit'),
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const resolveUniqueId = () => form.unique_id || unit.unique_id || null;

  const handleSave = (e) => {
    e.preventDefault();
    const unique_id = resolveUniqueId();
    if (!unique_id) {
      toast.error('Unit ID is missing');
      return;
    }
    const name = String(form.name || '').trim();
    if (!name) {
      toast.error('Enter a name for this unit');
      return;
    }

    saveMutation.mutate({
      ...form,
      unique_id,
      name,
      unit_type: String(form.unit_type || '').trim() || name,
      installation_year: form.installation_year ? Number(form.installation_year) : null,
      floor_plan_x: form.floor_plan_x !== '' ? Number(form.floor_plan_x) : null,
      floor_plan_y: form.floor_plan_y !== '' ? Number(form.floor_plan_y) : null,
      level_id: form.level_id || null,
      confirmed_checklist_json: confirmedChecklist,
      confirmed_checklist_meta: {
        confirmed_at: new Date().toISOString(),
        item_count: confirmedChecklist.length,
        sources: [...new Set(confirmedChecklist.map((i) => i.source || 'UNKNOWN'))],
      },
    });
  };

  const title = unitDisplayTitle({ ...unit, name: form.name, location: form.location || unit.instance_location });
  const suffixError = false;

  const shellClass = asPage
    ? 'bg-card rounded-xl shadow w-full'
    : 'bg-card rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto';

  const content = (
      <div
        data-unit-modal-scroll
        className={shellClass}
        onClick={asPage ? undefined : (e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 p-5 border-b">
          {asPage ? (
            <div className="min-w-0 flex-1" />
          ) : (
            <div className="min-w-0">
              <div className="flex items-baseline gap-2 flex-wrap">
                <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
                <InfoButton title="Unit hub">
                  <UnitHubInfoContent
                    unitIdHint={unit.unique_id || form.unique_id}
                    ssCode={unit.ss_code}
                  />
                </InfoButton>
              </div>
              <p className="text-sm text-gray-500">
                ID: {form.unique_id || unit.unique_id}
                {unit.ss_code ? ` · SS ${unit.ss_code}` : ''}
              </p>
            </div>
          )}
          <div className="flex items-center gap-2 shrink-0">
            <SsDefaultAssignee
              buildingId={building.id}
              instanceId={unit.building_ss_instance_id}
              ssCode={unit.ss_code}
              onSaved={() => {
                invalidate();
                onSaved?.();
              }}
            />
            {!asPage && building?.id && unit?.id && (
              <button
                type="button"
                title="Open full unit hub"
                onClick={() => navigate(`/buildings/${building.id}/units/${unit.id}`)}
                className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg"
              >
                <ExternalLink className="w-5 h-5" />
              </button>
            )}
            {!asPage && (
              <button type="button" onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        <form onSubmit={handleSave} className="p-5 space-y-4">
          {!asPage && hubTabs.length > 1 && (
            <div className="flex flex-wrap gap-1 -mt-1 mb-1 border-b border-gray-200">
              {hubTabs.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setHubTab(t.id)}
                  className={`px-3 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                    hubTab === t.id
                      ? 'bg-primary-50 text-primary-800 border border-b-white border-gray-200 -mb-px'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}

          {hubTab === 'system-info' && (
          <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4">
            <p className="text-lg font-semibold text-slate-900 mb-4">Set System</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Unit ID</label>
              <p className="w-full px-3 py-2 border rounded-lg font-mono bg-gray-50 text-gray-900">
                {formatUnitIdShort(form.unique_id || unit.unique_id, unit.ss_code) ||
                  form.unique_id || '—'}
              </p>
              <p className="text-xs text-gray-500 mt-1">Fixed when this unit was selected ??not editable.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Name for this unit"
              />
            </div>
            </div>

            <div className="mt-6 border-t border-slate-200 pt-4">
              <p className="text-sm font-semibold text-gray-900 mb-1">Floor plan equipment</p>
              <p className="text-xs text-gray-500 mb-3">
                Units placed on the floor plan for this type (e.g.{' '}
                {formatUnitIdShort(unit.unique_id, unit.ss_code)}-1, -2, ??.
              </p>
              {(floorUnits || []).length === 0 ? (
                <p className="text-sm text-gray-500 rounded-lg border border-dashed border-gray-200 px-3 py-4">
                  No floor-plan units yet. Place them on Building ??Floor plan.
                </p>
              ) : (
                <ul className="divide-y rounded-lg border border-slate-200 bg-card overflow-hidden">
                  {floorUnits.map((fu) => {
                    const inactive = String(fu.status || 'ACTIVE').toUpperCase() === 'INACTIVE';
                    const loc = resolveUnitOps(fu).location;
                    return (
                      <li key={fu.id}>
                        <button
                          type="button"
                          onClick={() => onOpenFloorUnit?.(fu)}
                          className={`w-full text-left px-4 py-3 flex items-baseline gap-3 hover:bg-primary-50 ${
                            inactive ? 'opacity-60' : ''
                          }`}
                        >
                          <span className="font-mono text-sm font-semibold text-primary-800 w-20 shrink-0">
                            {formatUnitIdShort(fu.unique_id, fu.ss_code || unit.ss_code)}
                          </span>
                          <span className="text-sm text-gray-900 min-w-0 truncate">
                            {fu.name || fu.unit_type || loc || '—'}
                          </span>
                          {inactive ? (
                            <span className="ml-auto text-[10px] uppercase text-gray-500">Inactive</span>
                          ) : null}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {asPage && (
              <AddSiblingUnitForm
                building={building}
                unit={unit}
                siblingUnits={siblingUnits}
                onCreated={() => {
                  invalidate();
                  onSaved?.();
                }}
              />
            )}

            <div className="mt-4 flex justify-end border-t border-slate-200 pt-4">
              <button
                type="submit"
                disabled={saveMutation.isPending || suffixError}
                className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 text-sm font-semibold"
              >
                <Save className="w-4 h-4 mr-2" />
                {saveMutation.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
          )}

          {hubTab === 'update-info' && (
            <div className="space-y-3 rounded-xl border border-amber-200 bg-amber-50/70 p-4">
              <p className="text-lg font-semibold text-amber-950">Unit photos & documents</p>
              <p className="text-xs text-amber-900/70">
                Take a photo or upload files for this unit (nameplate, manual, certificates).
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={uploading}
                  onClick={() => cameraInputRef.current?.click()}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
                >
                  <Camera className="w-4 h-4" />
                  Camera
                </button>
                <button
                  type="button"
                  disabled={uploading}
                  onClick={() => photoInputRef.current?.click()}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
                >
                  <Upload className="w-4 h-4" />
                  Upload photo
                </button>
                <button
                  type="button"
                  disabled={uploading}
                  onClick={() => docInputRef.current?.click()}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
                >
                  <FileText className="w-4 h-4" />
                  Upload document
                </button>
                {uploading && <span className="text-xs text-gray-500 self-center">Uploading…</span>}
              </div>
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  uploadUnitFile(f, 'manual_path');
                }}
              />
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  uploadUnitFile(f, 'manual_path');
                }}
              />
              <input
                ref={docInputRef}
                type="file"
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  uploadUnitFile(f, 'documents_path');
                }}
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Unit photo</label>
                  {form.manual_path ? (
                    <div className="space-y-1">
                      <div className="h-28 rounded-lg border bg-white overflow-hidden flex items-center justify-center">
                        <AuthImage
                          filename={form.manual_path}
                          alt="Unit photo"
                          className="max-h-28 max-w-full object-contain"
                        />
                      </div>
                      <button
                        type="button"
                        className="text-xs text-red-600"
                        onClick={() => setForm((p) => ({ ...p, manual_path: '' }))}
                      >
                        Remove photo
                      </button>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400 py-2">No photo yet</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Document</label>
                  {form.documents_path ? (
                    <div className="space-y-1">
                      <p className="text-xs text-gray-700 break-all bg-white border rounded-lg px-2 py-2">
                        {form.documents_path}
                      </p>
                      <button
                        type="button"
                        className="text-xs text-red-600"
                        onClick={() => setForm((p) => ({ ...p, documents_path: '' }))}
                      >
                        Remove document
                      </button>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400 py-2">No document yet</p>
                  )}
                </div>
              </div>
              <div className="flex justify-end border-t border-amber-200/80 pt-3">
                <button
                  type="submit"
                  disabled={saveMutation.isPending || suffixError}
                  className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 text-sm font-semibold"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saveMutation.isPending ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          )}

          {hubTab === 'set-inspection-list' && (
          <div className="space-y-4">
            <UnitChecklistPicker
              ssCode={unit.ss_code}
              buildingId={building.id}
              unitType={form.unit_type}
              value={confirmedChecklist}
              onChange={setConfirmedChecklist}
            />
            <div className="flex justify-end border-t pt-4">
              <button
                type="submit"
                disabled={saveMutation.isPending || suffixError}
                className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 text-sm font-semibold"
              >
                <Save className="w-4 h-4 mr-2" />
                {saveMutation.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
          )}

          <div className="flex justify-between pt-2 border-t">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  if (window.confirm(`Remove unit ${unit.unique_id}?`)) deleteMutation.mutate();
                }}
                disabled={deleteMutation.isPending}
                className="flex items-center px-4 py-2 text-red-600 border border-red-200 rounded-lg hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </button>
              {showRemoveFromPlan && onRemoveFromPlan && (
                <button
                  type="button"
                  onClick={() => onRemoveFromPlan(unit)}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Remove from plan
                </button>
              )}
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={onClose} className="px-4 py-2 border rounded-lg">
                {asPage ? 'Back' : 'Cancel'}
              </button>
              <button type="submit" disabled={saveMutation.isPending || suffixError} className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50">
                <Save className="w-4 h-4 mr-2" />
                {saveMutation.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </form>
      </div>
  );

  if (asPage) return content;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      {content}
    </div>
  );
}
